import { ReactNode } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { InputLabel } from '@/components/InputLabel';
import { PickerTrigger } from '@/components/PickerTrigger';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

import { cn } from '@/lib/utils';

type GridPickerItem = {
  key: string | number;
  label: string;
  isSelected: boolean;
  onSelect: () => void;
};

type GridPickerProps = {
  id: string;
  label?: ReactNode;
  hint?: ReactNode;
  isInvalid?: boolean;
  disabled?: boolean;
  className?: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  displayValue: string;
  placeholder?: string;
  clearButton?: ReactNode;
  title: ReactNode;
  onPrevious: () => void;
  onNext: () => void;
  items: GridPickerItem[];
};

const NAV_BUTTON_CLASS_NAME = 'size-10 text-muted-foreground [&_svg]:size-5';

const GridPicker = (props: GridPickerProps) => {
  const {
    id,
    label,
    hint,
    isInvalid,
    disabled,
    className,
    isOpen,
    onOpenChange,
    displayValue,
    placeholder,
    clearButton,
    title,
    onPrevious,
    onNext,
    items,
  } = props;

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {label && (
        <InputLabel
          htmlFor={id}
          label={label}
          hint={hint}
          isInvalid={isInvalid}
        />
      )}
      <Popover
        open={isOpen}
        onOpenChange={onOpenChange}
      >
        <PopoverTrigger asChild>
          <PickerTrigger
            id={id}
            disabled={disabled}
            isInvalid={isInvalid}
            isOpen={isOpen}
            displayValue={displayValue}
            placeholder={placeholder}
            clearButton={clearButton}
          />
        </PopoverTrigger>
        <PopoverContent
          variant="dropdown"
          className="w-(--radix-popover-trigger-width) max-w-105 min-w-85 rounded-[12px] p-4"
          align="start"
        >
          <div className="mb-3 flex items-center justify-between">
            <Button
              variant="ghost"
              size="icon"
              className={NAV_BUTTON_CLASS_NAME}
              onClick={onPrevious}
              type="button"
            >
              <ChevronLeft className="size-4" />
            </Button>
            <span className="text-sm font-medium">{title}</span>
            <Button
              variant="ghost"
              size="icon"
              className={NAV_BUTTON_CLASS_NAME}
              onClick={onNext}
              type="button"
            >
              <ChevronRight className="size-4" />
            </Button>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {items.map((item) => (
              <Button
                key={item.key}
                variant={item.isSelected ? 'default' : 'ghost'}
                className={cn(
                  'text-sm h-11 rounded-lg border-2 border-transparent',
                  item.isSelected &&
                    'border-primary bg-primary text-primary-foreground hover:bg-primary font-semibold',
                )}
                onClick={item.onSelect}
                type="button"
              >
                {item.label}
              </Button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export { GridPicker };
export type { GridPickerItem, GridPickerProps };
